import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RequiredLabel from "../../shared/RequiredLabel";
import ApiSelect from "../form/ApiSelect";
import AvatarPicker from "../form/AvatarPicker";

const modalStyle = {
  display: "flex",
  flexDirection: "column",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "758px",
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  pl: "78px",
  pb: "75px",
  gap: "25px",
};

const TextFieldStyle = {
  bgcolor: "#EEEEEE",
  width: "154px",
  pl: "20px",
  pr: "30px",
  py: "15px",
  borderRadius: 0,
  border: "1px solid transparent",
  "& .MuiInputBase-root": {
    padding: 0,
    boxShadow: "none !important",
    outline: "none !important",
  },
  "& .MuiInputBase-root.Mui-focused": {
    boxShadow: "none !important",
  },
  "& input": {
    fontSize: "14px",
    outline: "none !important",
    boxShadow: "none !important",
  },
  "&:focus-within": {
    boxShadow: "none !important",
    outline: "none !important",
  },
};

const UserModal = ({
  open,
  onClose,
  mode,
  initialValues,
  onSubmit,
  currentUser,
  allUsers,
  companyRoleKey,
}) => {
  const [number, setNumber] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastNameKana, setLastNameKana] = useState("");
  const [firstNameKana, setFirstNameKana] = useState("");
  const [email, setEmail] = useState("");
  const [prText, setPrText] = useState("");
  const [department_id, setDepartmentId] = useState(
    initialValues?.department_id ?? null
  );
  const [managerId, setManagerId] = useState(initialValues?.manager_id ?? null);
  const [errors, setErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const contentRef = useRef(null);
  const alertRef = useRef(null);

  // 全角カタカナ
  const KATAKANA_ONLY = /^[\u30A1-\u30F6\u30FC\uFF0D]+$/;

  const isEmployee = initialValues?.role === "employee";
  const isGeneral = initialValues?.role === "general";
  const isAdmin = initialValues?.role === "admin";

  const generalIndex = useMemo(() => {
    if (!Array.isArray(allUsers)) return "";
    const sorted = [...allUsers].sort((a, b) => a.id - b.id);
    const generals = sorted.filter((user) => user.role === "general");

    if (mode === "new" && initialValues?.generalIndex)
      return initialValues.generalIndex;
    if (initialValues?.generalIndex) return initialValues.generalIndex;

    const targetId = initialValues?.id;
    if (!targetId) return "";
    const index = generals.findIndex((user) => user.id === targetId);
    return index >= 0 ? index + 1 : "";
  }, [initialValues?.id, initialValues?.generalIndex, allUsers, mode]);

  const isOwnCard = currentUser?.id === initialValues?.id;
  const isTargetAdmin = isAdmin;
  const isCurrentGeneral = currentUser?.role === "general";

  const isNameDisabled = isEmployee
    ? false
    : (isCurrentGeneral && isOwnCard) || // generalは自分の氏名編集不可
      (!isOwnCard && isCurrentGeneral) || // generalは他人編集不可
      (currentUser?.role === "admin" && isTargetAdmin); // adminでもadmin相手は不可

  const isEmailDisabled = isEmployee
    ? false
    : (isCurrentGeneral && isOwnCard) || // generalは自分のメールアドレス編集不可
      (!isOwnCard && isCurrentGeneral) || // generalは他人編集不可
      (currentUser?.role === "admin" && isTargetAdmin); // adminでもadmin相手は不可

  const isPrTextDisabled = isEmployee ? false : !isOwnCard && isCurrentGeneral; // generalは他人編集不可

  const handleSubmit = async () => {
    const rules = [
      // 必須チェック
      {
        field: "lastName",
        condition: lastName.trim() === "",
        message: "姓は入力必須です。",
      },
      {
        field: "firstName",
        condition: firstName.trim() === "",
        message: "名は入力必須です。",
      },
      {
        field: "lastNameKana",
        condition: lastNameKana.trim() === "",
        message: "セイは入力必須です。",
      },
      {
        field: "firstNameKana",
        condition: firstNameKana.trim() === "",
        message: "メイは入力必須です。",
      },
      {
        field: "email",
        condition: email.trim() === "",
        message: "メールアドレスは入力必須です。",
      },

      // 全角カタカナチェック
      {
        field: "lastNameKana",
        condition:
          lastNameKana.trim() !== "" &&
          !KATAKANA_ONLY.test(lastNameKana.trim()),
        message: "セイは全角カタカナで入力してください。",
      },
      {
        field: "firstNameKana",
        condition:
          firstNameKana.trim() !== "" &&
          !KATAKANA_ONLY.test(firstNameKana.trim()),
        message: "メイは全角カタカナで入力してください。",
      },
    ];

    const newErrors = [];
    const fe = {};

    rules.forEach(({ field, condition, message }) => {
      if (condition) {
        newErrors.push(message);
        fe[field] = message; // フィールド別エラー（赤枠用）もセット
      }
    });

    setValidationTriggered(true);
    setErrors(newErrors);
    setFieldErrors(fe);
    if (newErrors.length > 0) return;

    // 以降は送信（ファイルも同梱するため FormData を使用）
    const fd = new FormData();

    // 編集時ID
    if (mode === "edit")
      fd.append("company_user[id]", String(initialValues?.id));

    // 社員番号
    fd.append("company_user[number]", number);

    // 氏名
    if (!isNameDisabled) {
      fd.append("company_user[last_name]", lastName);
      fd.append("company_user[first_name]", firstName);
      fd.append("company_user[last_name_kana]", lastNameKana);
      fd.append("company_user[first_name_kana]", firstNameKana);
    }
    // メール
    if (!isEmailDisabled) {
      fd.append("company_user[email]", email);
    }
    // 部署（department_id があれば常に送る）
    if (department_id != null) {
      fd.append(
        "company_user[department_membership_attributes][department_id]",
        String(department_id)
      );
    }
    // 担当者（未選択なら送らない）
    if (managerId != null && managerId !== "") {
      fd.append("company_user[manager_id]", String(managerId));
    }
    // PR文（employee は PR文送らない）
    if (!isEmployee) {
      fd.append("company_user[pr_text]", prText);
    }
    // 役割
    fd.append("company_user[role]", initialValues?.role);

    // employee かつ 画像が選択されている場合は送る
    if (isEmployee && avatar) {
      fd.append("company_user[avatar]", avatar);
    }

    // ×で削除された場合
    if (isEmployee && removeAvatar) {
      fd.append("company_user[remove_avatar]", "1");
    }

    try {
      await onSubmit(fd, { mode, id: initialValues?.id });
    } catch (errs) {
      const server = errs?.errors || errs;
      const nextFieldErrors = {};
      const flatMessages = [];

      if (server && typeof server === "object" && !Array.isArray(server)) {
        Object.entries(server).forEach(([attr, msgs]) => {
          const arr = Array.isArray(msgs) ? msgs : [msgs].filter(Boolean);
          flatMessages.push(...arr);
          const isEmailAttr =
            attr === "email" || attr.includes("メールアドレス");
          const isNumberAttr = attr === "number" || attr.includes("社員番号");
          if (isEmailAttr) nextFieldErrors.email = arr[0];
          if (isNumberAttr) nextFieldErrors.number = arr[0];
        });
      } else if (Array.isArray(server)) {
        // ["メールアドレスはすでに存在します。", ...]
        server.forEach((m) => {
          const s = String(m);
          flatMessages.push(s);
          if (s.includes("メールアドレス")) nextFieldErrors.email = s;
          if (s.includes("社員番号")) nextFieldErrors.number = s;
        });
      } else if (typeof server === "string") {
        flatMessages.push(server);
        if (server.includes("メールアドレス")) nextFieldErrors.email = server;
        if (server.includes("社員番号")) nextFieldErrors.number = server;
      } else {
        flatMessages.push("バリデーションエラーが発生しました。");
      }

      setFieldErrors(nextFieldErrors);
      setErrors(flatMessages);
      setValidationTriggered(true);

      // 上へスクロール＆アラートにフォーカス
      setTimeout(() => {
        contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        alertRef.current?.focus?.();
      }, 0);
    }
  };

  useEffect(() => {
    if (open && mode === "edit" && initialValues) {
      setNumber(initialValues.number || "");
      setLastName(initialValues.last_name || "");
      setFirstName(initialValues.first_name || "");
      setLastNameKana(initialValues.last_name_kana || "");
      setFirstNameKana(initialValues.first_name_kana || "");
      setEmail(initialValues.email || "");
      setPrText(initialValues.prText ?? "");
      setDepartmentId(
        initialValues.department_id ?? initialValues.departmentId ?? null
      );
      setManagerId(initialValues.manager_id ?? null);
      setAvatar(null);
      setAvatarUrl(initialValues.avatar_url ?? initialValues.avatarUrl ?? null);
    } else {
      setNumber("");
      setLastName("");
      setFirstName("");
      setLastNameKana("");
      setFirstNameKana("");
      setEmail("");
      setPrText("");
      setDepartmentId(null);
      setManagerId(null);
      setAvatar(null);
      setAvatarUrl(null);
    }
    if (!open) {
      setErrors([]);
      setFieldErrors({});
      setValidationTriggered(false);
    }
  }, [initialValues, mode, open]);

  // エラーが出たらモーダル内スクロールを先頭へ
  useEffect(() => {
    if (validationTriggered && errors.length > 0) {
      contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [validationTriggered, errors.length]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box ref={contentRef} sx={modalStyle}>
        <Box sx={{ textAlign: "right", mt: "8px" }}>
          <Button onClick={onClose}>
            <CloseIcon sx={{ color: "#767676" }} />
          </Button>
        </Box>

        {validationTriggered && errors.length > 0 && (
          <Alert
            ref={alertRef}
            tabIndex={-1}
            severity="error"
            sx={{
              width: "612px",
              bgcolor: "#FFF5F5",
              color: "#C53030",
              borderRadius: 2,
            }}
          >
            {errors.map((err, i) => (
              <div key={i}>{err}</div>
            ))}
          </Alert>
        )}

        {/* 見出し：役割に応じて表示 */}
        <Typography sx={{ fontSize: "24px", color: "#979797" }}>
          {isAdmin
            ? "代表者"
            : isGeneral && generalIndex
            ? `担当者${generalIndex}`
            : isEmployee
            ? "メンバー"
            : ""}
        </Typography>

        {/* 写真：employee のみ表示 */}
        {isEmployee && (
          <Box sx={{ width: { xs: "100%", md: 612 }, mb: "20px" }}>
            <AvatarPicker
              value={{ file: avatar, url: avatarUrl }}
              onChange={({ file, url }) => {
                setAvatar(file);
                setAvatarUrl(url);
                setRemoveAvatar(!file && !url);
              }}
              onError={(msg) => {
                setValidationTriggered(true);
                setErrors((prev) =>
                  prev.includes(msg) ? prev : [...prev, msg]
                );
              }}
            />
          </Box>
        )}

        {/* 社員番号 */}
        <Box>
          <Box sx={{ display: "flex" }}>
            <Typography sx={{ mb: "4px", mr: "16px" }}>社員番号</Typography>
          </Box>
          <TextField
            variant="standard"
            InputProps={{ disableUnderline: true }}
            error={Boolean(fieldErrors.number)}
            sx={(theme) => ({
              ...TextFieldStyle,
              width: "612px",
              border: `1px solid ${
                fieldErrors.number ? theme.palette.error.main : "transparent"
              }`,
            })}
            value={number}
            onChange={(e) => {
              setNumber(e.target.value);
              if (fieldErrors.number) {
                setFieldErrors((prev) => ({ ...prev, number: undefined }));
              }
            }}
            disabled={isCurrentGeneral}
          />
        </Box>

        {/* 氏名 */}
        <Box sx={{ width: { xs: "100%", md: 612 } }}>
          <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {/* 姓 */}
            <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 0" } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  mb: "4px",
                }}
              >
                <Typography>姓</Typography>
                <RequiredLabel />
              </Box>
              <TextField
                variant="standard"
                InputProps={{ disableUnderline: true }}
                error={Boolean(fieldErrors.lastName)}
                sx={(theme) => ({
                  ...TextFieldStyle,
                  width: "100%",
                  border: `1px solid ${
                    fieldErrors.lastName
                      ? theme.palette.error.main
                      : "transparent"
                  }`,
                })}
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (fieldErrors.lastName) {
                    setFieldErrors((prev) => ({
                      ...prev,
                      lastName: undefined,
                    }));
                  }
                }}
                disabled={isNameDisabled}
              />
            </Box>

            {/* 名 */}
            <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 0" } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  mb: "4px",
                }}
              >
                <Typography>名</Typography>
                <RequiredLabel />
              </Box>
              <TextField
                variant="standard"
                InputProps={{ disableUnderline: true }}
                error={Boolean(fieldErrors.firstName)}
                sx={(theme) => ({
                  ...TextFieldStyle,
                  width: "100%",
                  border: `1px solid ${
                    fieldErrors.firstName
                      ? theme.palette.error.main
                      : "transparent"
                  }`,
                })}
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (fieldErrors.firstName) {
                    setFieldErrors((prev) => ({
                      ...prev,
                      firstName: undefined,
                    }));
                  }
                }}
                disabled={isNameDisabled}
              />
            </Box>
          </Box>
        </Box>

        {/* 氏名(カナ) */}
        <Box sx={{ width: { xs: "100%", md: 612 } }}>
          <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {/* セイ */}
            <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 0" } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  mb: "4px",
                }}
              >
                <Typography>セイ</Typography>
                <RequiredLabel />
              </Box>
              <TextField
                variant="standard"
                InputProps={{ disableUnderline: true }}
                error={Boolean(fieldErrors.lastNameKana)}
                sx={(theme) => ({
                  ...TextFieldStyle,
                  width: "100%",
                  border: `1px solid ${
                    fieldErrors.lastNameKana
                      ? theme.palette.error.main
                      : "transparent"
                  }`,
                })}
                value={lastNameKana}
                onChange={(e) => {
                  setLastNameKana(e.target.value);
                  if (fieldErrors.lastNameKana) {
                    setFieldErrors((prev) => ({
                      ...prev,
                      lastNameKana: undefined,
                    }));
                  }
                }}
                disabled={isNameDisabled}
              />
            </Box>

            {/* メイ */}
            <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 0" } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  mb: "4px",
                }}
              >
                <Typography>メイ</Typography>
                <RequiredLabel />
              </Box>
              <TextField
                variant="standard"
                InputProps={{ disableUnderline: true }}
                error={Boolean(fieldErrors.firstNameKana)}
                sx={(theme) => ({
                  ...TextFieldStyle,
                  width: "100%",
                  border: `1px solid ${
                    fieldErrors.firstNameKana
                      ? theme.palette.error.main
                      : "transparent"
                  }`,
                })}
                value={firstNameKana}
                onChange={(e) => {
                  setFirstNameKana(e.target.value);
                  if (fieldErrors.firstNameKana) {
                    setFieldErrors((prev) => ({
                      ...prev,
                      firstNameKana: undefined,
                    }));
                  }
                }}
                disabled={isNameDisabled}
              />
            </Box>
          </Box>
        </Box>

        {/* メールアドレス（共通で必須） */}
        <Box>
          <Box sx={{ display: "flex" }}>
            <Typography sx={{ mb: "4px", mr: "16px" }}>
              メールアドレス
            </Typography>
            <RequiredLabel />
          </Box>
          <TextField
            variant="standard"
            InputProps={{ disableUnderline: true }}
            error={Boolean(fieldErrors.email)}
            sx={(theme) => ({
              ...TextFieldStyle,
              width: "612px",
              border: `1px solid ${
                fieldErrors.email ? theme.palette.error.main : "transparent"
              }`,
            })}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (fieldErrors.email) {
                setFieldErrors((prev) => ({
                  ...prev,
                  email: undefined,
                }));
              }
            }}
            disabled={isEmailDisabled}
          />
        </Box>

        {/* 担当者：employeeのみ表示 */}
        {isEmployee && (
          <Box>
            <ApiSelect
              value={managerId ?? ""}
              onChange={(val) => setManagerId(val === "" ? null : Number(val))}
              endpoint="/company/users.json"
              label="担当者"
              isRequired={false}
              error={false}
              blankLabel="未設定"
            />
          </Box>
        )}

        {/* 部署：利用目的がマネジメントの企業のみ表示 */}
        {companyRoleKey === "management" && (
          <Box>
            <ApiSelect
              value={department_id ?? ""}
              onChange={(val) =>
                setDepartmentId(val === "" || val == null ? null : val)
              }
              endpoint="/company/settings.json"
              label="部署"
              isRequired={false}
              error={false}
              blankLabel="未設定"
            />
          </Box>
        )}

        {/* PR文：employee では非表示 */}
        {!isEmployee && (
          <Box>
            <Typography sx={{ mb: "4px" }}>PR文（最大70字まで）</Typography>
            <TextField
              multiline
              variant="standard"
              value={prText}
              onChange={(e) => setPrText(e.target.value)}
              disabled={isPrTextDisabled}
              inputProps={{ maxLength: 70 }}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
              sx={{
                bgcolor: "#EEEEEE",
                width: "612px",
                height: "86px",
                pl: "20px",
                pr: "30px",
                borderRadius: 0,
                "& textarea": {
                  outline: "none !important",
                  boxShadow: "none !important",
                },
                "& .MuiInputBase-root": {
                  py: "15px",
                  boxShadow: "none !important",
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  padding: 0,
                  lineHeight: "28px",
                  resize: "none",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                },
                "& .MuiInputBase-root.Mui-focused": {
                  boxShadow: "none !important",
                },
                "& input": {
                  outline: "none !important",
                  boxShadow: "none !important",
                },
                "&:focus-within": {
                  boxShadow: "none !important",
                  outline: "none !important",
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none !important",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none !important",
                },
              }}
            />
            <Typography
              sx={{
                textAlign: "right",
                mt: "4px",
                fontSize: "12px",
                color: "#767676",
                width: "612px",
              }}
            >
              {`${prText.length} 文字`}
            </Typography>
          </Box>
        )}

        <Box>
          <Button
            onClick={onClose}
            sx={{
              px: "25px",
              py: "10px",
              border: "1px solid #454545",
              borderRadius: 0,
              color: "#000",
              mr: "23px",
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{
              px: "20px",
              py: "11px",
              bgcolor: "#292929",
              color: "#FFF",
              borderRadius: 0,
              "&:hover": { bgcolor: "#666666" },
            }}
          >
            {mode === "edit" ? "更新" : "追加"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UserModal;
